export type PhoneCountry = {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
};

export type PhoneCountryCode = (typeof PHONE_COUNTRIES)[number]['code'];

export const PHONE_COUNTRIES = [
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
] as const satisfies readonly PhoneCountry[];

export const DEFAULT_PHONE_COUNTRY_CODE: PhoneCountryCode = 'IN';

export const PHONE_COUNTRY_SELECT_OPTIONS = PHONE_COUNTRIES.map((country) => ({
  value: country.code,
  label: `${country.flag} ${country.dialCode} · ${country.name}`,
}));

const COUNTRY_BY_CODE = new Map<string, PhoneCountry>(
  PHONE_COUNTRIES.map((country) => [country.code, country] as const)
);

const COUNTRIES_BY_DIAL_LENGTH = [...PHONE_COUNTRIES].sort((left, right) => {
  return right.dialCode.replace(/\D/g, '').length - left.dialCode.replace(/\D/g, '').length;
});

export function normalizePhoneDigits(value: string) {
  return String(value || '').replace(/\D/g, '').trim();
}

export function getPhoneCountry(countryCode: string | null | undefined) {
  return (
    COUNTRY_BY_CODE.get(String(countryCode || '').trim().toUpperCase()) ??
    COUNTRY_BY_CODE.get(DEFAULT_PHONE_COUNTRY_CODE) ??
    PHONE_COUNTRIES[0]
  );
}

export function splitPhoneNumber(value: string) {
  const trimmed = String(value || '').trim();

  if (!trimmed) {
    return {
      countryCode: DEFAULT_PHONE_COUNTRY_CODE,
      phoneNumber: '',
    };
  }

  const digits = normalizePhoneDigits(trimmed);

  if (!digits) {
    return {
      countryCode: DEFAULT_PHONE_COUNTRY_CODE,
      phoneNumber: '',
    };
  }

  if (trimmed.startsWith('+') || trimmed.startsWith('00')) {
    for (const country of COUNTRIES_BY_DIAL_LENGTH) {
      const dialDigits = normalizePhoneDigits(country.dialCode);

      if (digits.startsWith(dialDigits) && digits.length > dialDigits.length) {
        return {
          countryCode: country.code,
          phoneNumber: digits.slice(dialDigits.length),
        };
      }
    }
  }

  return {
    countryCode: DEFAULT_PHONE_COUNTRY_CODE,
    phoneNumber: digits,
  };
}

export function joinPhoneNumber(countryCode: PhoneCountryCode, phoneNumber: string) {
  const country = getPhoneCountry(countryCode);
  const digits = normalizePhoneDigits(phoneNumber);

  if (!digits) {
    return '';
  }

  return `${country.dialCode} ${digits}`;
}
